import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import PremiumHero from '../components/PremiumHero';
import HiddenPageMessage from '../components/HiddenPageMessage';
import PreviewBanner from '../components/PreviewBanner';
import MediaGallerySection from '../components/MediaGallerySection';
import DocumentViewerSection from '../components/DocumentViewerSection';
import SpeakerPreviewSection from '../components/SpeakerPreviewSection';
import SponsorsSection from '../components/SponsorsSection';
import FeaturedNationSection from '../components/FeaturedNationSection';
import ProgramHighlightsSection from '../components/ProgramHighlightsSection';
import { DesignerSection, DESIGNER_SECTION_TYPES } from '../components/DesignerSections';
import { useVisibility } from '../hooks/useVisibility';
import { usePreviewMode, fetchWithPreview } from '../hooks/usePreviewMode';
import { Button } from '../components/ui/button';
import { Mail, Phone, Check, Star, Award, Users, Building, Coffee, Presentation, Globe, ChevronDown, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Fallback sponsorship tier data - used when CMS data is unavailable
const fallbackSponsorshipTiers = [
  {
    id: 'founding',
    name: 'Founding Institutional Sponsor',
    price: '$20,000',
    priceValue: 20000,
    description: 'The Founding Institutional Sponsor is recognized as a cornerstone supporter of the World Nuclear Congress.',
    includes: [
      'Exclusive recognition as Founding Institutional Sponsor',
      'Standalone, full-screen logo recognition at opening and closing',
      'Top-tier logo placement on the WNC website and program materials',
      'Verbal recognition during opening and closing ceremonies',
      'Complimentary registrations for 8 guests',
      'One complimentary WNC Engagement Showcase exhibit space',
      'Two invitations to the private VIP dinner (limited, curated)',
      'Opportunity to include one approved item in welcome materials',
      'Recognition in communications and archival materials',
      'Opportunity to present keynote speaker introduction and appreciation gift'
    ],
    featured: true,
    availability: 'Limited'
  },
  {
    id: 'premier',
    name: 'Premier Congress Sponsor',
    price: '$11,500',
    priceValue: 11500,
    description: 'Premier Sponsors support the Congress as a whole and are closely associated with its leadership and mission.',
    includes: [
      'Prominent logo placement on website and program',
      'Large logo recognition on shared on-screen sponsor displays',
      'Verbal recognition during opening and closing sessions',
      'Complimentary registrations for 5 guests',
      'Or one complimentary Engagement Showcase Exhibit space and 1 complimentary registration',
      'One invitation to the private VIP dinner (limited, curated)',
      'One complimentary WNC Engagement Showcase exhibit space',
      'Opportunity to include one approved item in welcome materials'
    ],
    featured: true,
    availability: 'Limited'
  },
  {
    id: 'strategic',
    name: 'Strategic Program Sponsor',
    price: '$6,500',
    priceValue: 6500,
    description: 'Strategic Sponsors align with major program elements.',
    availableOpportunities: [
      'Opening Reception',
      'Distinguished Lunch & Learn',
      'Inside the Hill Studio (full program block)',
      'International Alliance',
      'Closing Ceremony & Reception',
      'Media & Production Underwriter',
      'Access & Participation Underwriter'
    ],
    includes: [
      'Logo placement tied to the sponsored program element',
      'Medium–large logo recognition on shared sponsor screens',
      'Website and program recognition',
      'Complimentary registrations for 3 guests',
      'OR one complimentary WNC Engagement Showcase exhibit space and one complimentary registration',
      'Verbal acknowledgment during the sponsored session'
    ],
    featured: false,
    availability: 'Available'
  },
  {
    id: 'session',
    name: 'Session Sponsor',
    price: '$2,750',
    priceValue: 2750,
    description: 'Session Sponsors support core thematic discussions.',
    availableOpportunities: [
      'Global Deployment Summit',
      'Global Lessons, U.S. Builds & Fuel Readiness (entire three-part session)',
      'Financing the Nuclear Renaissance',
      'Nuclear for Defense & Deterrence',
      'State Energy Leadership Roundtable and Expanded Continental Breakfast'
    ],
    includes: [
      'Logo recognition during the sponsored session',
      'Website and program listing',
      'Complimentary registrations for 1 guest'
    ],
    featured: false,
    availability: 'Available'
  },
  {
    id: 'supporting',
    name: 'Supporting Sponsor',
    price: '$2,750',
    priceValue: 2750,
    description: 'Supporting Sponsors contribute directly to the attendee experience.',
    availableOpportunities: [
      'Coffee & Conversation Breaks',
      'Green Room Support',
      'Presentation & Environment (stage, flags, décor)'
    ],
    includes: [
      'Logo recognition on shared sponsor screens',
      'Website and program listing',
      'Complimentary registration for 1 guest'
    ],
    featured: false,
    availability: 'Available'
  }
];

// Fallback exhibit showcase benefits
const fallbackExhibitBenefits = [
  'Two (2) complimentary registrations to attend the full World Nuclear Congress',
  'Logo recognition on the WNC website and in select program materials',
  'On-screen logo recognition during Engagement Showcase networking breaks',
  'Opportunity to include one approved item in the WNC welcome materials',
  'A dedicated exhibit space designed for meaningful engagement',
  'Direct interaction during structured networking breaks, including scheduled times when attendees are encouraged to connect with showcasing organizations',
  'Interaction with invited graduate students, who will visit showcase spaces to learn about organizations shaping the future of nuclear energy'
];

// Fallback why participate bullets
const fallbackWhyParticipateBullets = [
  'Engage decision-makers in focused networking windows',
  'Solidify your presence in the nuclear community as a thought leader',
  'Connect with top nuclear engineering students exploring career and collaboration opportunities',
  'Build relationships that extend beyond sessions into follow-on dialogue'
];

// Fallback sponsor at a glance data
const fallbackSponsorGlance = [
  { price: '$20,000', name: 'Founding Institutional Sponsor' },
  { price: '$11,500', name: 'Premier Congress Sponsor' },
  { price: '$6,500', name: 'Strategic Program Sponsor' },
  { price: '$2,750', name: 'Session Sponsor' },
  { price: '$2,750', name: 'Supporting Sponsor' }
];

// Sponsorship Card Component
const SponsorshipCard = ({ tier }) => {
  return (
    <div 
      className={`rounded-2xl border-2 transition-all duration-300 hover:shadow-xl ${
        tier.featured 
          ? 'border-yellow-400 bg-gradient-to-br from-white to-yellow-50 shadow-lg' 
          : 'border-gray-200 bg-white hover:border-[#00457f]/30'
      }`}
    >
      {tier.featured && (
        <div className="bg-gradient-to-r from-yellow-500 to-amber-500 text-white text-center py-2 rounded-t-xl font-semibold text-sm">
          <Star className="w-4 h-4 inline mr-2" />
          Featured Opportunity
        </div>
      )}
      <div className="p-6 md:p-8">
        <div className="flex justify-between items-start mb-4">
          <h3 className="text-xl md:text-2xl font-bold text-[#00457f]">{tier.name}</h3>
          <span className="text-2xl md:text-3xl font-bold text-yellow-600">{tier.price}</span>
        </div>
        
        <p className="text-gray-700 mb-6">{tier.description}</p>
        
        {tier.availableOpportunities && (
          <div className="mb-6">
            <p className="font-semibold text-gray-800 mb-2">Available Opportunities Include:</p>
            <ul className="space-y-1">
              {tier.availableOpportunities.map((opp, idx) => (
                <li key={idx} className="flex items-start text-gray-600 text-sm">
                  <span className="text-yellow-500 mr-2">•</span>
                  {opp}
                </li>
              ))}
            </ul>
          </div>
        )}
        
        <div>
          <p className="font-semibold text-gray-800 mb-3">Includes:</p>
          <ul className="space-y-2">
            {tier.includes.map((benefit, idx) => (
              <li key={idx} className="flex items-start text-gray-700 text-sm">
                <Check className="w-4 h-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                {benefit}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

// Main Sponsorship Page Component
const SponsorshipPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [cmsContent, setCmsContent] = useState(null);
  const [sponsorshipTiers, setSponsorshipTiers] = useState(fallbackSponsorshipTiers);
  const [exhibitBenefits, setExhibitBenefits] = useState(fallbackExhibitBenefits);
  const [whyParticipateBullets, setWhyParticipateBullets] = useState(fallbackWhyParticipateBullets);
  const [sponsorGlance, setSponsorGlance] = useState(fallbackSponsorGlance);
  const [contacts, setContacts] = useState([
    { name: 'Michelle', email: 'michelle@ceeleaders.com', phone: '+1 423-421-2825', role: 'Primary Contact' },
    { name: 'Nastasia', email: 'nastasia@ceeleaders.com', phone: null, role: 'Additional Contact' }
  ]);
  
  // Check page visibility
  const { isPageVisible, getPageHiddenMessage, loading: visibilityLoading } = useVisibility();
  const { preview } = usePreviewMode();
  const [isDraft, setIsDraft] = useState(false);
  const [extraSections, setExtraSections] = useState([]);

  // Fetch CMS content
  useEffect(() => {
    let ignore = false;
    const fetchContent = async () => {
      try {
        const [{ data, isDraft: draft }, extraRes] = await Promise.all([
          fetchWithPreview(`${API_URL}/api/content/sponsorship`, { previewEnabled: preview }),
          fetchWithPreview(`${API_URL}/api/homepage/public?page=sponsorship`, { previewEnabled: preview }),
        ]);
        if (ignore) return;
        setIsDraft(draft);
        setExtraSections(extraRes?.data?.sections || []);
        if (data) {
          setCmsContent(data);

          // Transform CMS tiers to component format if available
          if (data.sponsorship_tiers && data.sponsorship_tiers.length > 0) {
            const transformedTiers = data.sponsorship_tiers.map((tier, idx) => ({
              id: tier.id,
              name: tier.name,
              price: tier.price,
              priceValue: parseInt(tier.price.replace(/[^0-9]/g, '')) || 0,
              description: tier.description,
              includes: tier.benefits || [],
              featured: idx < 2, // First two tiers are featured
              availability: idx < 2 ? 'Limited' : 'Available'
            }));
            setSponsorshipTiers(transformedTiers);
            
            // Update sponsor glance
            setSponsorGlance(transformedTiers.map(t => ({
              price: t.price,
              name: t.name
            })));
          }

          // Update exhibit info if available
          if (data.exhibit_info) {
            if (data.exhibit_info.includes && data.exhibit_info.includes.length > 0) {
              setExhibitBenefits(data.exhibit_info.includes);
            }
            if (data.exhibit_info.why_participate && data.exhibit_info.why_participate.length > 0) {
              setWhyParticipateBullets(data.exhibit_info.why_participate);
            }
          }

          // Update contacts if available
          if (data.contacts && data.contacts.length > 0) {
            setContacts(data.contacts);
          }
        }
      } catch (error) {
        console.error('Failed to fetch sponsorship content:', error);
        // Fallback data is already set as default
      } finally {
        setLoading(false);
      }
    };
    fetchContent();
    return () => { ignore = true; };
  }, [preview]);

  useEffect(() => {
    // Handle hash navigation - scroll to section if hash exists
    const hash = window.location.hash;
    if (hash) {
      // Wait for content to load before scrolling
      setTimeout(() => {
        const element = document.getElementById(hash.replace('#', ''));
        if (element) {
          const offset = 100;
          const top = element.getBoundingClientRect().top + window.pageYOffset - offset;
          window.scrollTo({ top, behavior: 'smooth' });
        }
      }, 300);
    } else {
      window.scrollTo(0, 0);
    }
  }, []);

  // If page is hidden, show the hidden message (placed after all hooks)
  if (!visibilityLoading && !isPageVisible('sponsorship')) {
    const message = getPageHiddenMessage('sponsorship');
    return (
      <HiddenPageMessage
        header={message.header}
        subtitle={message.subtitle}
        body={message.body}
      />
    );
  }

  const scrollToContact = () => {
    const contactSection = document.getElementById('contact-section');
    if (contactSection) {
      const offset = 100;
      const top = contactSection.getBoundingClientRect().top + window.pageYOffset - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  const handleEmailMichelle = () => {
    window.location.href = 'mailto:michelle@ceeleaders.com?subject=WNC - Sponsorship Inquiry';
  };

  const handleEmailNastasia = () => {
    window.location.href = 'mailto:nastasia@ceeleaders.com?subject=WNC - Sponsorship Inquiry';
  };

  const handleExhibitEmail = () => {
    window.location.href = 'mailto:michelle@ceeleaders.com?subject=WNC - Engagement Showcase Exhibit Inquiry';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation showBackButton={true} />
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-[#00457f]" />
        </div>
      </div>
    );
  }

  /* ─── Section JSX keyed by section_order values ──── */
  /* eslint-disable react/no-unstable-nested-components */
  const sectionRenderers = {
    hero: () => (
      <React.Fragment key="hero">
        <PremiumHero
          title={cmsContent?.hero_title || 'Sponsorship and Exhibit Showcase'}
          subtitle={cmsContent?.hero_subtitle || 'The Independent U.S. Forum for Global Nuclear Progress'}
          overline={cmsContent?.hero_eyebrow || 'World Nuclear Congress 2027'}
          testId="hero-title"
          editScope={{ type: 'sponsorship' }}
        >
          <div className="flex flex-col gap-4 items-center">
            <Button
              onClick={scrollToContact}
              className="bg-gradient-to-r from-yellow-500 via-amber-500 to-yellow-600 hover:from-yellow-600 hover:via-amber-600 hover:to-yellow-700 text-white font-bold text-lg px-10 py-6 rounded-full transition-all duration-300 transform hover:scale-105 shadow-xl"
              style={{ boxShadow: '0 10px 40px rgba(245, 158, 11, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.3)' }}
              data-testid="hero-cta-btn"
            >
              <Mail className="mr-2 w-5 h-5" />
              Request Sponsorship Information
            </Button>
            <Button
              onClick={() => document.getElementById('exhibits')?.scrollIntoView({ behavior: 'smooth' })}
              variant="outline"
              className="border-2 border-white/50 text-white hover:bg-white/10 font-bold text-lg px-10 py-6 rounded-full transition-all duration-300"
              data-testid="hero-exhibits-btn"
            >
              View Exhibit Options
            </Button>
          </div>
        </PremiumHero>
      </React.Fragment>
    ),
    about: () => (
      <section key="about" className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          {cmsContent?.about_eyebrow && (
            <p className="text-[#00457f]/60 text-sm font-semibold uppercase tracking-[0.2em] mb-3 text-center" data-testid="about-eyebrow">{cmsContent.about_eyebrow}</p>
          )}
          <h2 className="text-3xl md:text-4xl font-bold text-[#00457f] mb-3 text-center" data-testid="about-section-title">
            {cmsContent?.section_title_about || 'About the World Nuclear Congress'}
          </h2>
          {cmsContent?.about_subtitle && (
            <p className="text-lg text-slate-500 max-w-2xl mx-auto text-center mb-8 font-light" data-testid="about-subtitle">{cmsContent.about_subtitle}</p>
          )}
          {!cmsContent?.about_subtitle && <div className="mb-8" />}
          <div className="space-y-6 text-gray-700 leading-relaxed">
            {cmsContent?.about_text ? (
              cmsContent.about_text.split('\n').filter(Boolean).map((p, i) => (
                <p key={i} className={p.startsWith('At Congressional') || p.startsWith('We look') ? 'italic text-[#00457f] font-medium' : ''}>
                  {p}
                </p>
              ))
            ) : (
              <>
                <p>
                  The World Nuclear Congress (WNC) is an independent, U.S.-based international forum for global nuclear progress. The WNC is convened by Congressional Energy Engagement (CEE) as its flagship international platform and brings together U.S. leadership, allied nations, and global participants—including policymakers, U.S. and global industry leaders, embassy representatives, national laboratories, and academic institutions—to advance nuclear energy deployment, energy security, and international collaboration.
                </p>
                <p>
                  By intentionally bringing global perspectives into a U.S. setting, the Congress &ldquo;brings the world home,&rdquo; creating a space where experienced international leaders and U.S. stakeholders engage directly, share insights, and work together to make meaningful, collective progress on the opportunities and challenges shaping nuclear energy today.
                </p>
                <p>
                  Each year, the World Nuclear Congress highlights one allied nation as a focal country for deeper dialogue. Romania will serve as the Featured Allied Nation, reflecting its leadership in nuclear deployment and its strong relationship with the United States, while welcoming participation and perspectives from additional allied nations and embassy representatives throughout the Congress.
                </p>
                <p>
                  Designed as a trusted, dialogue-driven convening, the World Nuclear Congress connects policy with deployment, innovation with readiness, and national priorities with global experience—creating an environment where ideas advance through conversation, relationships are strengthened, and shared objectives take form.
                </p>
                <p className="italic text-[#00457f] font-medium">
                  At Congressional Energy Engagement we believe that &ldquo;Human achievement is inseparable from Human interaction.&rdquo;
                </p>
                <p>
                  The Congress will be hosted on the campus of a nationally recognized U.S. nuclear university, providing a neutral and collaborative setting for dialogue, exchange, and forward-looking engagement among global nuclear leaders.
                </p>
                <p className="font-semibold text-[#00457f]">
                  We look forward to welcoming you!
                </p>
              </>
            )}
          </div>
        </div>
      </section>
    ),
    whySponsor: () => {
      const defaultPoints = [
        'Advance serious, solutions-oriented dialogue on nuclear energy, security and prosperity and meaningful deployment',
        'Demonstrate commitment to global engagement and allied cooperation',
        'Invest in high-impact convenings that bridge policy, innovation, and implementation',
        'Help accelerate nuclear deployment readiness and strengthen shared credibility',
        'Support constructive collaboration among government, industry, academia, and international partners',
      ];
      const pointIcons = [Globe, Users, Award, Building, Presentation];
      const points = cmsContent?.why_sponsor_points?.length ? cmsContent.why_sponsor_points : defaultPoints;
      const intro = cmsContent?.why_sponsor_intro;

      return (
      <section key="whySponsor" className="py-16 bg-gradient-to-br from-[#001a2e] via-[#00457f] to-[#003461]">
        <div className="max-w-6xl mx-auto px-6">
          {(cmsContent?.why_sponsor_eyebrow) && (
            <p className="text-amber-400 text-sm font-semibold uppercase tracking-[0.2em] mb-3 text-center" data-testid="why-sponsor-eyebrow">
              {cmsContent.why_sponsor_eyebrow}
            </p>
          )}
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-3 text-center" data-testid="why-sponsor-title">
            {cmsContent?.section_title_whySponsor || 'Why Sponsor the World Nuclear Congress'}
          </h2>
          {(cmsContent?.why_sponsor_subtitle) && (
            <p className="text-lg text-slate-300 max-w-2xl mx-auto text-center mb-10 font-light" data-testid="why-sponsor-subtitle">
              {cmsContent.why_sponsor_subtitle}
            </p>
          )}
          {!(cmsContent?.why_sponsor_subtitle) && <div className="mb-8" />}
          <div className="grid md:grid-cols-2 gap-10">
            <div className="space-y-6 text-gray-200 leading-relaxed">
              {intro ? (
                intro.split('\n').filter(Boolean).map((p, i) => <p key={i}>{p}</p>)
              ) : (
                <>
                  <p>
                    Sponsoring the World Nuclear Congress reflects alignment with leadership, credibility, and long-term impact—offering meaningful visibility within a trusted global forum shaping the future of nuclear deployment, energy security, and allied cooperation both in the United States and around the world. It is a platform for global visibility and influence.
                  </p>
                  <p>
                    Sponsoring the World Nuclear Congress (WNC) reflects alignment with leadership, credibility, and long-term impact—while offering meaningful visibility within a highly trusted, international forum.
                  </p>
                </>
              )}
            </div>
            <div>
              <ul className="space-y-4">
                {points.map((point, i) => {
                  const Icon = pointIcons[i % pointIcons.length];
                  return (
                    <li key={i} className="flex items-start">
                      <span className="bg-amber-500/20 text-amber-400 rounded-full p-2 mr-4 flex-shrink-0">
                        <Icon className="w-5 h-5" />
                      </span>
                      <span className="text-gray-200">{point}</span>
                    </li>
                  );
                })}
              </ul>
              <p className="mt-6 text-gray-400 italic">
                We maintain a focused sponsor roster to ensure meaningful visibility and engagement for each supporter.
              </p>
            </div>
          </div>
        </div>
      </section>
      );
    },
    tiers: () => (
      <React.Fragment key="tiers">
      <section id="sponsorship-tiers-section" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          {cmsContent?.tiers_eyebrow && (
            <p className="text-[#00457f]/60 text-sm font-semibold uppercase tracking-[0.2em] mb-3 text-center" data-testid="tiers-eyebrow">{cmsContent.tiers_eyebrow}</p>
          )}
          <h2 className="text-3xl md:text-4xl font-bold text-[#00457f] mb-3 text-center" data-testid="sponsorship-levels-title">
            {cmsContent?.section_title_tiers || 'Sponsorship Levels & Opportunities'}
          </h2>
          {cmsContent?.tiers_subtitle && (
            <p className="text-lg text-slate-500 max-w-2xl mx-auto text-center mb-6 font-light" data-testid="tiers-subtitle">{cmsContent.tiers_subtitle}</p>
          )}
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
            Choose the sponsorship level that aligns with your organization&apos;s goals and visibility needs.
          </p>
          
          {/* Featured Tiers */}
          <div className="grid md:grid-cols-2 gap-8 mb-8">
            {sponsorshipTiers.filter(t => t.featured).map((tier) => (
              <SponsorshipCard key={tier.id} tier={tier} />
            ))}
          </div>
          
          {/* Standard Tiers */}
          <div className="grid md:grid-cols-3 gap-6">
            {sponsorshipTiers.filter(t => !t.featured).map((tier) => (
              <SponsorshipCard key={tier.id} tier={tier} />
            ))}
          </div>
        </div>
      </section>

      {/* 5. SPONSOR AT A GLANCE */}
      <section className="py-12 bg-gradient-to-r from-[#00457f] to-[#003461]">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-8 text-center" data-testid="sponsor-glance-title">
            {cmsContent?.section_title_glance || 'Sponsorships at a Glance'}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {sponsorGlance.map((item, idx) => (
              <button
                key={idx}
                onClick={() => {
                  const el = document.getElementById('sponsorship-tiers-section');
                  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
                className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center border border-white/20 hover:bg-white/20 hover:border-white/40 transition-all cursor-pointer"
                data-testid={`glance-box-${idx}`}
              >
                <p className="text-2xl md:text-3xl font-bold text-yellow-400 mb-2">{item.price}</p>
                <p className="text-white text-sm">{item.name}</p>
              </button>
            ))}
          </div>
          <p className="text-center text-gray-300 mt-6 text-sm">
            All sponsors receive recognition in post-event communications.
          </p>
        </div>
      </section>
      </React.Fragment>
    ),
    exhibits: () => (
      <React.Fragment key="exhibits">
      <section id="exhibits" className="py-16 bg-white scroll-mt-20">
        <div className="max-w-4xl mx-auto px-6">
          {cmsContent?.exhibits_eyebrow && (
            <p className="text-[#00457f]/60 text-sm font-semibold uppercase tracking-[0.2em] mb-3 text-center" data-testid="exhibits-eyebrow">{cmsContent.exhibits_eyebrow}</p>
          )}
          <h2 className="text-3xl md:text-4xl font-bold text-[#00457f] mb-3 text-center" data-testid="engagement-showcase-title">
            {cmsContent?.section_title_exhibits || 'WNC Engagement Showcase Exhibit Spaces'}
          </h2>
          {cmsContent?.exhibits_subtitle && (
            <p className="text-lg text-slate-500 max-w-2xl mx-auto text-center mb-8 font-light" data-testid="exhibits-subtitle">{cmsContent.exhibits_subtitle}</p>
          )}
          {!cmsContent?.exhibits_subtitle && <div className="mb-8" />}
          <div className="space-y-6 text-gray-700 leading-relaxed">
            <p>
              The World Nuclear Congress Engagement Showcase takes the exhibit experience above the traditional model. It is a curated alternative to a traditional exhibit hall—designed for purposeful engagement as well as visibility. It is an intentional space where organizations connect directly with global leaders, attendees, and emerging talent during the Congress engagement breaks.
            </p>
            <p>
              Exhibitors are positioned as active contributors to the dialogue, engaging with leaders, policymakers, industry executives, national laboratory experts, and academic partners during designated networking periods such as Coffee & Conversation breaks. These breaks are structured to encourage direct interaction through brief bilateral meetings, conversations with showcasing organizations, and intentional engagement with nuclear engineering graduate students and future talent.
            </p>
            <p className="font-medium text-[#00457f]">
              This is a curated engagement space where sponsors connect with global leaders, attendees, and top graduate students through structured networking breaks that encourage direct conversations and future opportunities.
            </p>
          </div>
        </div>
      </section>

      {/* 7. WHAT EXHIBITORS RECEIVE */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-[#00457f] mb-8 text-center" data-testid="exhibitors-receive-title">
            {cmsContent?.section_title_exhibitIncludes || 'What an Engagement Showcase Exhibit Includes'}
          </h2>
          <p className="text-gray-700 mb-8 text-center">
            Each Engagement Showcase Exhibitor receives:
          </p>
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <ul className="space-y-4">
              {exhibitBenefits.map((benefit, idx) => (
                <li key={idx} className="flex items-start">
                  <Check className="w-6 h-6 text-green-500 mr-4 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{benefit}</span>
                </li>
              ))}
            </ul>
            <p className="mt-8 text-gray-600 italic border-t pt-6">
              Exhibit placement is intentionally limited to preserve quality engagement and visibility. Placement is on a first-come, first-serve basis.
            </p>
          </div>
        </div>
      </section>

      {/* 8. WHY PARTICIPATE */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-[#00457f] mb-8 text-center" data-testid="why-participate-title">
            {cmsContent?.section_title_whyParticipate || 'Why Participate in the Engagement Showcase'}
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {whyParticipateBullets.map((bullet, idx) => (
              <div key={idx} className="flex items-start bg-gray-50 rounded-xl p-6">
                <span className="bg-[#00457f] text-white rounded-full p-2 mr-4 flex-shrink-0">
                  <Check className="w-5 h-5" />
                </span>
                <span className="text-gray-700">{bullet}</span>
              </div>
            ))}
          </div>
          <p className="mt-8 text-gray-600 text-center italic">
            Exhibitors are encouraged to design their space around conversation and insight, whether highlighting technology, strategy, workforce development, or future partnerships.
          </p>
        </div>
      </section>

      {/* 9. PRICING & AVAILABILITY */}
      <section className="py-12 bg-gradient-to-r from-yellow-500 to-amber-500">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4" data-testid="pricing-availability-title">
            {cmsContent?.section_title_pricing || 'Pricing & Availability'}
          </h2>
          <div className="bg-white rounded-2xl shadow-xl p-8 inline-block">
            <p className="text-4xl md:text-5xl font-bold text-[#00457f] mb-2">
              Starting at {cmsContent?.exhibit_info?.price || '$3,500'}
            </p>
            <p className="text-xl text-gray-800 font-semibold">for Engagement Showcase Exhibit Space</p>
            <p className="text-gray-600 mt-4 italic">
              Limited availability to maintain a curated experience
            </p>
          </div>
        </div>
      </section>
      </React.Fragment>
    ),
    contacts: () => (
      <React.Fragment key="contacts">
      <section id="contact-section" className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[#00457f] mb-6" data-testid="contact-section-title">
            {cmsContent?.section_title_contacts || 'Interested in Sponsoring?'}
          </h2>
          <p className="text-gray-700 mb-4">
            Detailed sponsorship information, benefits, and availability are available upon request.
          </p>
          <p className="text-gray-700 mb-8">
            For special, customized sponsorship arrangements or to design a package aligned with your goals, please contact us directly.
          </p>
          
          <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
            <div className={`grid gap-8 ${contacts.length === 1 ? 'grid-cols-1 max-w-sm mx-auto' : contacts.length === 2 ? 'md:grid-cols-2' : 'md:grid-cols-2 lg:grid-cols-3'}`}>
              {contacts.map((contact, idx) => (
                <div key={idx} data-testid={`contact-card-${idx}`}>
                  {contact.name && (
                    <p className="font-bold text-[#0a1a2f] text-lg mb-0.5">{contact.name}</p>
                  )}
                  <p className="text-gray-500 text-sm mb-3">{contact.role || 'Contact'}</p>
                  <a 
                    href={`mailto:${contact.email}`}
                    className="text-[#00457f] font-semibold hover:underline flex items-center justify-center gap-2"
                  >
                    <Mail className="w-4 h-4" />
                    {contact.email}
                  </a>
                  {contact.phone && (
                    <a 
                      href={`tel:${contact.phone.replace(/[^+\d]/g, '')}`}
                      className="text-[#00457f] font-semibold hover:underline flex items-center justify-center gap-2 mt-2"
                    >
                      <Phone className="w-4 h-4" />
                      {contact.phone}
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => window.location.href = `mailto:${contacts[0]?.email || 'contact@usceef.org'}?subject=WNC 2027 - Sponsorship Inquiry`}
              className="bg-gradient-to-r from-yellow-500 via-amber-500 to-yellow-600 hover:from-yellow-600 hover:via-amber-600 hover:to-yellow-700 text-white font-bold text-lg px-10 py-6 rounded-full transition-all duration-300 transform hover:scale-105 shadow-xl"
              style={{
                boxShadow: '0 10px 40px rgba(245, 158, 11, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.3)'
              }}
              data-testid="contact-cta-btn"
            >
              <Mail className="mr-2 w-5 h-5" />
              Contact Us About Sponsorship
            </Button>
            <Button
              onClick={() => window.location.href = `mailto:${contacts[0]?.email || 'contact@usceef.org'}?subject=WNC 2027 - Engagement Showcase Exhibit Inquiry`}
              variant="outline"
              className="border-2 border-[#00457f] text-[#00457f] hover:bg-[#00457f] hover:text-white font-bold text-lg px-10 py-6 rounded-full transition-all duration-300"
              data-testid="exhibit-cta-btn"
            >
              <Building className="mr-2 w-5 h-5" />
              Inquire About Exhibit Space
            </Button>
          </div>

          <p className="text-gray-500 text-sm mt-8">
            For exhibit details, or to explore customized participation arrangements, please contact us directly.
          </p>
        </div>
      </section>
      </React.Fragment>
    ),
  };

  // Build a map of extra sections by ID for quick lookup
  const extraMap = {};
  extraSections.forEach(s => { extraMap[s.id] = s; });

  // Unified section order: CMS keys + extra section IDs, from the saved order
  const DEFAULT_CMS = ['hero', 'about', 'whySponsor', 'tiers', 'exhibits', 'contacts'];
  const storedOrder = cmsContent?.section_order;
  const extraIds = extraSections.map(s => s.id);

  let fullOrder;
  if (storedOrder && storedOrder.length > 0) {
    // Include any missing sections that might have been added after the order was saved
    const missing = [...DEFAULT_CMS, ...extraIds].filter(k => !storedOrder.includes(k));
    fullOrder = [...storedOrder, ...missing];
  } else {
    fullOrder = [...DEFAULT_CMS, ...extraIds];
  }

  // Filter out hidden CMS sections and hidden extra sections
  const visibleOrder = fullOrder.filter(key => {
    if (DEFAULT_CMS.includes(key)) {
      return !cmsContent?.[`section_hidden_${key}`];
    }
    const extra = extraMap[key];
    return extra && extra.visible !== false;
  });

  const renderExtraSection = (s) => {
    if (s.section_type === 'media_gallery') return <MediaGallerySection key={s.id} content={s.content || {}} sectionId={s.id} />;
    if (s.section_type === 'document_viewer') return <DocumentViewerSection key={s.id} content={s.content || {}} />;
    if (s.section_type === 'speaker_preview') return <SpeakerPreviewSection key={s.id} content={s.content || {}} />;
    if (s.section_type === 'sponsors') return <SponsorsSection key={s.id} content={s.content || {}} />;
    if (s.section_type === 'featured_nation') return <FeaturedNationSection key={s.id} content={s.content || {}} />;
    if (s.section_type === 'program_highlights') return <ProgramHighlightsSection key={s.id} content={s.content || {}} />;
    if (DESIGNER_SECTION_TYPES.has(s.section_type)) return <DesignerSection key={s.id} section={s} />;
    return null;
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="sponsorship-page">
      <PreviewBanner isDraft={isDraft} draftMeta={isDraft ? { updated_at: new Date().toISOString() } : null} pageLabel="Sponsorship" />
      <Navigation showBackButton={true} />

      {/* Unified section rendering — CMS sections and template sections in one flow */}
      {visibleOrder.map((key) => {
        // CMS section
        if (DEFAULT_CMS.includes(key)) {
          return sectionRenderers[key]?.() || null;
        }
        // Extra/template section
        const extra = extraMap[key];
        return extra ? renderExtraSection(extra) : null;
      })}

      <Footer />
    </div>
  );
};

export default SponsorshipPage;
